import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { DiscoverUser } from '../../types/discover';
import { useDiscover } from '../../hooks/useDiscover';
import Avatar from '../common/Avatar';
import MatchInvitationModal from '../common/MatchInvitationModal';
import SignUpPromptModal from '../common/SignUpPromptModal';
import { occupations } from '../../constants/formData';

interface DiscoverUserCardProps {
  user: DiscoverUser;
  onNavigate: (page: string) => void;
  isSuggestion?: boolean;
  isAuthenticated?: boolean;
}

const DiscoverUserCard: React.FC<DiscoverUserCardProps> = ({
  user,
  onNavigate,
  isSuggestion = false,
  isAuthenticated = true
}) => {
  const { sendMatchInvitation } = useDiscover();
  const [isLoading, setIsLoading] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  // Format YYYY-MM-01 to "June 2026"; falls back to raw string for legacy enum values
  const formatMoveInDate = (moveIn: string): string => {
    if (!moveIn) return moveIn;
    const d = new Date(moveIn + 'T00:00:00');
    if (isNaN(d.getTime())) return moveIn;
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleOpenInvitationModal = () => {
    if (!isAuthenticated) {
      setShowSignUpModal(true);
      return;
    }
    setShowInvitationModal(true);
  };

  const handleViewProfile = () => {
    if (!isAuthenticated) {
      setShowSignUpModal(true);
      return;
    }
    onNavigate(`profile/view/${user.id}`);
  };

  const handleSendInvitation = async (message: string) => {
    setIsLoading(true);
    try {
      const result = await sendMatchInvitation(user.id, message || undefined);
      if (result.success) {
        // Close modal on success
        setShowInvitationModal(false);
        // Optionally show success message (could add toast notification here)
        alert('Invitation sent successfully!');
      } else {
        // Show error message
        alert(result.error || 'Failed to send invitation. Please try again.');
      }
    } catch (error) {
      console.error('Send invitation failed:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Avatar
                profilePictureUrl={user.user.profilePicture ?? null}
                firstName={user.user.firstName}
                lastName={user.user.lastName}
                userId={user.user.id}
                size="medium"
              />
              {user.user.verified && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-terracotta rounded-full flex items-center justify-center border border-coffee">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
              {user.isOnline && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-cream"></div>
              )}
            </div>
            <div>
              <h3 className="text-coffee font-bold text-lg">
                {user.user.firstName} {user.user.lastName}
              </h3>
              <p className="text-coffee/70 text-sm">
                {user.user.age}{user.occupation ? ` • ${occupations.find(o => o.value === user.occupation)?.label || user.occupation}` : ''}
              </p>
              {user.distance && (
                <p className="text-coffee/60 text-xs">
                  {user.distance} miles away
                </p>
              )}
            </div>
          </div>
          {isAuthenticated && (
            <div className="text-right">
              <div className="bg-terracotta text-white font-bold rounded-lg px-3 py-1 text-lg border border-coffee shadow-sm">
                {user.compatibility.overall}%
              </div>
              <div className="text-coffee/70 text-xs mt-1">Match</div>
            </div>
          )}
        </div>

        {/* Bio */}
        {user.user.bio && (
          <div className="mb-4">
            <p className="text-coffee/90 text-sm line-clamp-2">
              {user.user.bio}
            </p>
          </div>
        )}

        {/* Location (conspicuous, where score details were) */}
        {user.preferences.location && user.preferences.location.length > 0 && (
          <div className="mb-4">
            <div className="text-coffee font-semibold text-base mb-2">Location</div>
            <div className="flex flex-wrap gap-1">
              {user.preferences.location.map((loc) => (
                <span key={loc.id ?? `${loc.borough}-${loc.neighborhood}`} className="bg-white border border-coffee/30 text-coffee px-2 py-1 rounded text-xs">
                  {loc.neighborhood}{loc.borough ? `, ${loc.borough}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preferences Preview */}
        {(user.preferences.budget || user.preferences.moveInDate) && (
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-3">
              {user.preferences.budget && (
                <div>
                  <div className="text-coffee font-semibold text-base mb-2">Budget</div>
                  <span className="bg-white border border-coffee/30 text-coffee px-2 py-1 rounded text-xs">
                    {user.preferences.budget}
                  </span>
                </div>
              )}
              {user.preferences.moveInDate && (
                <div>
                  <div className="text-coffee font-semibold text-base mb-2">Move-in</div>
                  <span className="bg-white border border-coffee/30 text-coffee px-2 py-1 rounded text-xs">
                    {formatMoveInDate(user.preferences.moveInDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Interests */}
        {user.interests.length > 0 && (
          <div className="mb-4">
            <div className="text-coffee/80 font-medium text-sm mb-2">Interests:</div>
            <div className="flex flex-wrap gap-1">
              {user.interests.slice(0, 3).map((interest, index) => (
                <span key={index} className="bg-white border border-coffee/30 text-coffee px-2 py-1 rounded text-xs">
                  {interest}
                </span>
              ))}
              {user.interests.length > 3 && (
                <span className="text-coffee/70 text-xs">
                  +{user.interests.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions: Send Invitation, View Profile — single row */}
        <div className="flex gap-3">
          <button
            onClick={handleOpenInvitationModal}
            disabled={isLoading}
            className="flex-1 text-sm bg-terracotta text-white border-2 border-coffee rounded-lg shadow-[2px_2px_0px_#594A42] hover:translate-y-[-1px] active:translate-y-[1px] active:shadow-none transition-all disabled:opacity-50 px-3 py-2 font-medium flex items-center justify-center gap-1.5 whitespace-nowrap"
          >
            <Mail className="h-4 w-4 shrink-0" />
            Send Invitation
          </button>
          <button
            type="button"
            onClick={handleViewProfile}
            disabled={isLoading}
            className="flex-1 text-sm bg-cream border-2 border-coffee/30 text-coffee rounded-lg hover:bg-coffee/10 transition-all disabled:opacity-50 px-3 py-2 font-medium flex items-center justify-center whitespace-nowrap"
          >
            View Profile
          </button>
        </div>
      </div>

      {/* Match Invitation Modal */}
      <MatchInvitationModal
        user={user}
        isOpen={showInvitationModal}
        onClose={() => setShowInvitationModal(false)}
        onSendInvitation={handleSendInvitation}
        loading={isLoading}
      />

      {/* Sign-Up Prompt Modal (guests only) */}
      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        message="Sign up to view profiles and connect with potential roommates"
      />
    </>
  );
};

export default DiscoverUserCard;
